import { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { FormDialog, FormDialogBody, FormDialogContent, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../components/ui/form-dialog';
import { FormMessage } from '../../components/ui/form-message';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ResponsiveTableRow, useTabelaEmContextoTablet } from '../../components/ui/responsive-table-row';
import {
  estadoDoBotaoExecutarEstorno,
  executarEstornoExpedicao,
  listarSolicitacoesEstorno,
  type SolicitacaoEstornoApi,
} from '../../lib/expedicao';
import { formatarDataHora, formatarQuantidade } from '../../lib/vendas/formatacao';

interface SessaoEstorno { readonly userId: string | null; readonly permissoes: readonly string[] }

function numero(texto: string): number { const valor = Number(texto); return Number.isFinite(valor) ? valor : 0; }

// Regra visível ao usuário: "Quem solicitou não executa (segregação de funções)." (texto em lib/expedicao.ts).
/** Lista pura (sem estado) das solicitações pendentes: facilita provar escondido/desabilitado sem montar a página. */
export function ListaEstornosPendentes({ solicitacoes, sessao, onExecutar }: {
  readonly solicitacoes: readonly SolicitacaoEstornoApi[];
  readonly sessao: SessaoEstorno;
  readonly onExecutar: (solicitacao: SolicitacaoEstornoApi) => void;
}) {
  const tabelaEmTablet = useTabelaEmContextoTablet();
  const pendentes = solicitacoes.filter((item) => item.estado === 'PENDENTE_REVISAO');
  if (pendentes.length === 0) return <p className="p-6 text-center text-muted-foreground">Não há correções de saída pendentes.</p>;
  return (
    <div>
      <table className="w-full text-sm">
        <caption className="sr-only">Solicitações de correção de saída pendentes</caption>
        <thead className="bg-muted/50 text-left">
          <tr>
            <th scope="col" className="p-3">PV</th>
            <th scope="col" className="p-3">Lote</th>
            {!tabelaEmTablet && <th scope="col" className="p-3">Quantidade</th>}
            {!tabelaEmTablet && <th scope="col" className="p-3">Solicitante</th>}
            <th scope="col" className="p-3">Motivo</th>
            {!tabelaEmTablet && <th scope="col" className="p-3">Solicitada em</th>}
            <th scope="col" className="p-3"><span className="sr-only">Ações</span></th>
            {tabelaEmTablet && <th scope="col" className="p-2"><span className="sr-only">Detalhes</span></th>}
          </tr>
        </thead>
        <tbody>
          {pendentes.map((item) => {
            const botao = estadoDoBotaoExecutarEstorno(item, sessao);
            const dicaId = `estorno-motivo-${item.id}`;
            const acao = botao.visivel ? <div className="grid justify-items-end gap-1"><Button type="button" variant="outline" className="min-h-11" disabled={!botao.habilitado} aria-describedby={botao.motivo ? dicaId : undefined} onClick={() => onExecutar(item)}>Executar estorno</Button>{botao.motivo && <span id={dicaId} className="max-w-56 text-xs text-muted-foreground">{botao.motivo}</span>}</div> : <span className="text-xs text-muted-foreground">Sem permissão</span>;
            return <ResponsiveTableRow
              key={item.id}
              rowId={`estorno-${item.id}`}
              className="border-t align-top"
              primaryCellIds={['pv', 'lote', 'motivo', 'acoes']}
              cells={[
                { id: 'pv', label: 'PV', content: item.pvNumero ?? 'PV não informado' },
                { id: 'lote', label: 'Lote', content: item.loteCodigo ?? 'Lote não informado' },
                { id: 'quantidade', label: 'Quantidade', content: formatarQuantidade(numero(item.quantidade)) },
                { id: 'solicitante', label: 'Solicitante', content: item.solicitanteNome ?? 'Não informado' },
                { id: 'motivo', label: 'Motivo', content: <span className="break-words">{item.justificativaSolicitacao}</span> },
                { id: 'data', label: 'Solicitada em', content: formatarDataHora(item.solicitadaEm) },
                { id: 'acoes', label: 'Ações', content: acao, className: 'text-right' },
              ]}
            />;
          })}
        </tbody>
      </table>
    </div>
  );
}

export function ExecutarEstornoDialog({ solicitacao, onFechar, onExecutado }: {
  readonly solicitacao: SolicitacaoEstornoApi | null;
  readonly onFechar: () => void;
  readonly onExecutado: () => void;
}) {
  const [justificativa, setJustificativa] = useState('');
  const [materialLocal, setMaterialLocal] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => { setJustificativa(''); setMaterialLocal(false); setErro(null); setEnviando(false); }, [solicitacao?.id]);

  async function executar(): Promise<void> {
    if (!solicitacao || !justificativa.trim() || !materialLocal || enviando) return;
    setEnviando(true); setErro(null);
    try {
      const resultado = await executarEstornoExpedicao(solicitacao, { justificativa, materialPermaneceLocal: materialLocal });
      if (!resultado.ok) { setErro(resultado.mensagem); return; }
      onExecutado();
    } catch {
      setErro('O resultado da execução é incerto. Recarregue a lista antes de repetir.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <FormDialog open={solicitacao !== null} onOpenChange={(aberto) => { if (!aberto) onFechar(); }}>
      <FormDialogContent>
        <FormDialogHeader><FormDialogTitle>Executar estorno de saída</FormDialogTitle></FormDialogHeader>
        <FormDialogBody className="space-y-3">
          {solicitacao && <p>O estorno devolve ao estoque a saída física do lote {solicitacao.loteCodigo ?? 'informado'} ({formatarQuantidade(numero(solicitacao.quantidade))}) do PV {solicitacao.pvNumero ?? 'não informado'} como um novo fato auditável; a saída original continua no histórico.</p>}
          <div className="grid gap-1">
            <Label htmlFor="estorno-justificativa">Justificativa</Label>
            <Textarea id="estorno-justificativa" value={justificativa} onChange={(evento) => setJustificativa(evento.target.value)} disabled={enviando} />
          </div>
          <div className="flex items-start gap-2">
            <input id="estorno-material-local" type="checkbox" className="mt-1 size-4" checked={materialLocal} onChange={(evento) => setMaterialLocal(evento.target.checked)} disabled={enviando} />
            <Label htmlFor="estorno-material-local">Confirmo que o material permanece fisicamente na Pieltec.</Label>
          </div>
          {erro && <FormMessage>{erro}</FormMessage>}
        </FormDialogBody>
        <FormDialogFooter>
          <Button type="button" variant="outline" onClick={onFechar} disabled={enviando}>Cancelar</Button>
          <Button type="button" disabled={!justificativa.trim() || !materialLocal || enviando} onClick={() => void executar()}>{enviando && <Loader2 aria-hidden="true" className="animate-spin" />} Confirmar estorno</Button>
        </FormDialogFooter>
      </FormDialogContent>
    </FormDialog>
  );
}

/** Seção "Correções de saída pendentes" da Expedição (L-56). Visível com `expedicao:consultar`. */
export function EstornosPendentes({ sessao, onExecutado }: { readonly sessao: SessaoEstorno; readonly onExecutado?: () => void }) {
  const [solicitacoes, setSolicitacoes] = useState<readonly SolicitacaoEstornoApi[] | null>(null);
  const [erro, setErro] = useState(false);
  const [selecionada, setSelecionada] = useState<SolicitacaoEstornoApi | null>(null);
  const podeConsultar = sessao.permissoes.includes('expedicao:consultar');

  const carregar = useCallback(async () => {
    setErro(false);
    try { setSolicitacoes(await listarSolicitacoesEstorno()); } catch { setErro(true); }
  }, []);

  useEffect(() => { if (podeConsultar) void carregar(); }, [podeConsultar, carregar]);

  if (!podeConsultar) return null;
  return (
    <Card data-testid="estornos-pendentes">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Correções de saída pendentes</CardTitle>
        <Button type="button" variant="outline" onClick={() => void carregar()}><RefreshCw aria-hidden="true" /> Atualizar</Button>
      </CardHeader>
      <CardContent className="p-0">
        {erro && <p role="alert" className="p-6 text-center">Não foi possível carregar as correções pendentes. Tente atualizar.</p>}
        {!erro && solicitacoes === null && <p className="p-6 text-center text-muted-foreground" aria-busy="true">Carregando correções pendentes…</p>}
        {!erro && solicitacoes !== null && <ListaEstornosPendentes solicitacoes={solicitacoes} sessao={sessao} onExecutar={setSelecionada} />}
      </CardContent>
      <ExecutarEstornoDialog
        solicitacao={selecionada}
        onFechar={() => setSelecionada(null)}
        onExecutado={() => { setSelecionada(null); void carregar(); onExecutado?.(); }}
      />
    </Card>
  );
}
